/**
 * Review-crops dialog (round-8 rework): SEE THEN CONFIRM. Still the full-page
 * takeover with the responsive card grid (round 7), but resolving a card no
 * longer removes it — every card the session started with stays in the grid
 * showing the SAVED outcome (bright kept region for crops, real white-space
 * sheet for fits) under a green ✓ state chip, so "Auto-crop all" / "Fit all"
 * can be eyeballed and re-toggled before pressing Done. Every choice applies
 * to the store immediately; Done (and Esc) simply close. "Accept all" is the
 * one exception — it confirms everything and closes in one go. Removing a
 * print still drops its card (the print is gone).
 *
 * DRAG-TO-CROP (round 8): the live preview on ratio cards is draggable — the
 * kept region slides across the photo along its slack axis (a centered
 * auto-crop has slack on exactly one axis; a zoomed custom crop can move on
 * both), live under the pointer, and persists as a custom crop on release
 * (lib/cropMath.dragCropRect → actions.setCustomCrop). Pointer events, so
 * touch works too. ✎ Adjust still opens the full editor.
 *
 * SESSION SET: seeded with the prints flagged when the dialog opens (narrowed
 * to `scopeIds` for the wizard handoff), so cards resolved before opening
 * don't flood the grid; anything that becomes flagged mid-session joins. The
 * banner / Continue counts elsewhere key off flaggedForReview and keep
 * updating live as cards resolve.
 */

import { parseSizeId } from '@rpl/pricing';
import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { bwCssFilter } from '../../lib/bw';
import { dragAxisFor, dragCropRect } from '../../lib/cropMath';
import { centeredAutoCropRect, isPanoInches, ratioFit } from '../../lib/printCheck';
import {
  fitOptionVisible,
  flaggedForReview,
  isBlockedPrint,
  isRatioUnresolved,
  reviewCardState,
  seedReviewSession,
  sessionReviewCards,
  sizeShort, sizeWithUnit,
  trimOverlay,
} from '../../lib/sizePaper';
import type { CropRect, LocalPhoto, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { ModalShell } from './ModalShell';
import type { OrderActions } from './useOrderActions';

export interface ReviewCropsModalProps {
  actions: OrderActions;
  onOpenCrop: (printId: string) => void;
  onClose: () => void;
  /** Limit the session to these print ids (wizard handoff). Omit/null = all flagged. */
  scopeIds?: readonly string[] | null;
}

interface CardDrag {
  startX: number;
  startY: number;
  startRect: CropRect;
  dispW: number;
  dispH: number;
  /** Flips once the pointer travels a few px — a plain click never saves. */
  moved: boolean;
}

/**
 * One review card: live preview + per-print fixes. Exported for SSR tests.
 * Never leaves the grid when resolved — it flips to the ✓-chip look instead.
 */
export function ReviewCard({
  print,
  photo,
  actions,
  onOpenCrop,
}: {
  print: UiPrint;
  photo: LocalPhoto | undefined;
  actions: OrderActions;
  onOpenCrop: (printId: string) => void;
}) {
  const size = sizeShort(parseSizeId(print.sizeId).size);
  const entry = actions.catalog.get(print.sizeId);
  const state = reviewCardState(print, photo, entry?.inches);
  const blocked = state.kind === 'blocked';
  const dims = photo && photo.width > 0 && photo.height > 0 ? { w: photo.width, h: photo.height } : null;
  const rotated = (print.rotation ?? 0) !== 0;

  // Live crop-window preview (kept region bright, trims dimmed) for crop-mode
  // cards: the saved rect, or the exact centered auto-crop the bake would
  // apply. Rotated prints skip the overlay — their rect lives in rotated
  // space, which the unrotated thumbnail can't draw truthfully.
  const cropRect =
    !blocked && print.fitMode === 'crop' && entry && dims && !rotated
      ? print.cropRect ?? centeredAutoCropRect(dims.w, dims.h, entry.inches)
      : null;

  /* ---------- drag-to-crop: slide the kept region, save on release ---------- */
  const [dragRect, setDragRect] = useState<CropRect | null>(null);
  const dragRef = useRef<CardDrag | null>(null);
  const draggable = !!cropRect && dragAxisFor(cropRect) !== 'none';

  const beginDrag = (e: ReactPointerEvent<HTMLElement>) => {
    if (!draggable || !cropRect) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // The imgbox shrink-wraps the img exactly, so its box IS the displayed
    // image size — the px→normalized conversion for dragCropRect.
    const box = e.currentTarget.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRect: cropRect,
      dispW: box.width,
      dispH: box.height,
      moved: false,
    };
  };

  const dragMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return; // still a click
    d.moved = true;
    setDragRect(dragCropRect(d.startRect, dx, dy, d.dispW, d.dispH));
  };

  const endDrag = (e: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    if (d.moved) {
      // Persist the slide as a custom crop (recheck keeps DPI/status honest).
      const final = dragCropRect(
        d.startRect,
        e.clientX - d.startX,
        e.clientY - d.startY,
        d.dispW,
        d.dispH,
      );
      actions.setCustomCrop(print.id, final);
    }
    setDragRect(null);
  };

  const cancelDrag = () => {
    dragRef.current = null;
    setDragRect(null);
  };

  const shownRect = dragRect ?? cropRect;
  const trim = shownRect ? trimOverlay(shownRect) : null;

  /* ---------- fit cards: the real sheet — whole photo + white space ---------- */
  let sheetStyle: CSSProperties | null = null;
  if (!blocked && print.fitMode === 'fit' && entry && dims && !rotated) {
    const lo = Math.min(entry.inches[0], entry.inches[1]);
    const hi = Math.max(entry.inches[0], entry.inches[1]);
    // Orientation follows the photo; panorama sizes always preview wide.
    const landscape = isPanoInches(entry.inches) ? true : dims.w >= dims.h;
    const w = landscape ? hi : lo;
    const h = landscape ? lo : hi;
    sheetStyle = { aspectRatio: `${w} / ${h}`, '--rv-ar': String(w / h) } as CSSProperties;
  }

  // Reserve the true image footprint before the (lazy) preview loads so the
  // strip overlay's percentages always map onto the image, never a square.
  const imgStyle: CSSProperties = dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : {};
  const img = photo?.previewUrl ? (
    <img src={photo.previewUrl} alt="" style={imgStyle} loading="lazy" draggable={false} />
  ) : (
    <span className="rvcard__ph" />
  );

  // No size-swap for framed prints — the frame pairing is fixed to the print
  // size (fr-{code}-{printSize}); Remove / Print anyway are the resolutions.
  const swapTarget =
    print.printCheck === 'lowres' && !print.frameId ? actions.swapTargetFor(print) : null;
  // "Fit instead" only when fitting would actually change anything.
  const fitPossible =
    entry && dims
      ? fitOptionVisible(dims.w, dims.h, entry.inches, actions.thresholds.ratioTolerance)
      : false;

  /* ---------- plain-language line under the filename ---------- */
  const hint = blocked
    ? swapTarget
      ? `Not enough pixels for a sharp ${size}″. ${sizeWithUnit(parseSizeId(swapTarget.sizeId).size)} is the largest that prints sharply, or print it at ${size}″ anyway`
      : `Not enough pixels for a sharp ${size}″. Print it anyway, or remove it`
    : state.kind === 'needs-look'
      ? `The dimmed edges are trimmed to fit ${size}″. Drag the photo to move the crop`
      : state.chip === 'Fit'
        ? `Whole photo prints at ${size}″, with white space filling the gaps`
        : state.chip === 'Printing anyway'
          ? `Low quality, printing at ${size}″ anyway`
          : state.chip === 'No crop needed'
            ? `Fits ${size}″ exactly. Nothing is trimmed`
            : draggable
              ? `Prints as shown at ${size}″. Drag to change what’s kept`
              : `Prints as shown at ${size}″`;

  return (
    <div className={`rvcard${blocked ? ' rvcard--err' : ''}`}>
      {/* B&W prints preview grayscale here too (round-11 fix). */}
      <div className={`rvcard__stage${print.bw && (!print.bwStyle || print.bwStyle === 'v1') ? ' rvcard__stage--bw' : ''}`}>
        {sheetStyle ? (
          <span className="rvcard__sheet" style={sheetStyle}>
            {photo?.previewUrl ? (
              <img
                src={photo.previewUrl}
                alt=""
                loading="lazy"
                draggable={false}
                style={print.bw ? { filter: bwCssFilter(true, print.bwStyle ?? null, print.bwStrength ?? null) } : undefined}
              />
            ) : (
              <span className="rvcard__ph" />
            )}
          </span>
        ) : (
          <span
            className={`rvcard__imgbox${draggable ? ' rvcard__imgbox--drag' : ''}`}
            {...(draggable
              ? {
                  onPointerDown: beginDrag,
                  onPointerMove: dragMove,
                  onPointerUp: endDrag,
                  onPointerCancel: cancelDrag,
                }
              : {})}
          >
            {img}
            {trim && (
              <>
                {trim.strips.map((s, i) => (
                  <span key={i} className="rvcard__strip" style={s as CSSProperties} aria-hidden />
                ))}
                <span className="rvcard__cropline" style={trim.boundary as CSSProperties} aria-hidden />
              </>
            )}
          </span>
        )}
      </div>
      <div className="rvcard__info">
        <span className="rvcard__file" title={photo?.filename}>
          {photo?.filename ?? 'photo'}
        </span>
        {state.kind === 'resolved' && state.chip && (
          <span
            className={`rvcard__chip${state.chip === 'Printing anyway' ? ' rvcard__chip--warn' : ''}`}
          >
            ✓ {state.chip}
          </span>
        )}
        <span className={`rvcard__size${blocked ? ' rvcard__size--err' : ''}`}>{hint}</span>
      </div>
      <div className="rvcard__btns">
        {blocked || state.chip === 'Printing anyway' ? (
          /* Owner 2026-07-27: keep the sale — "Print anyway" is the SOLID
             black action (they chose this size deliberately), with the
             recommended size named beside it and Remove last. */
          <>
            {blocked && (
              <button
                type="button"
                className="pill-btn pill-btn--solid"
                onClick={() => actions.printAnyway(print.id)}
              >
                Print anyway at {size}&quot;
              </button>
            )}
            {swapTarget && (
              <button type="button" className="pill-btn" onClick={() => actions.swapToMax(print.id)}>
                Recommended: {sizeWithUnit(parseSizeId(swapTarget.sizeId).size)}
              </button>
            )}
            <button type="button" className="pill-btn" onClick={() => actions.removeSize(print.id)}>
              Remove
            </button>
          </>
        ) : (
          <>
            {state.kind === 'needs-look' && (
              <button
                type="button"
                className="pill-btn rvcard__ok"
                onClick={() => actions.setCrop(print.id)}
              >
                ✓ Looks good
              </button>
            )}
            {state.chip === 'Fit' ? (
              <button type="button" className="pill-btn" onClick={() => actions.setCrop(print.id)}>
                Crop instead
              </button>
            ) : (
              (state.kind === 'needs-look' || fitPossible) && (
                <button type="button" className="pill-btn" onClick={() => actions.setFit(print.id)}>
                  Fit instead
                </button>
              )
            )}
            <button type="button" className="pill-btn" onClick={() => onOpenCrop(print.id)}>
              ✎ Adjust
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ReviewCropsModal({ actions, onOpenCrop, onClose, scopeIds }: ReviewCropsModalProps) {
  const prints = useStore((s) => s.prints);
  const photos = useStore((s) => s.photos);

  // SESSION set: seeded once from the flagged prints at open (scoped), then
  // grown with anything that becomes flagged mid-session (e.g. a swap that
  // still mismatches). Growing a Set during render is idempotent, so this is
  // StrictMode/SSR-safe.
  const sessionRef = useRef<Set<string> | null>(null);
  if (sessionRef.current === null) sessionRef.current = seedReviewSession(prints, scopeIds);
  const session = sessionRef.current;
  const scopeSet = useMemo(() => (scopeIds ? new Set(scopeIds) : null), [scopeIds]);
  for (const p of flaggedForReview(prints)) {
    if (!scopeSet || scopeSet.has(p.id)) session.add(p.id);
  }

  const cards = sessionReviewCards(prints, session);
  const photoById = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);

  const stillFlagged = cards.filter((p) => isBlockedPrint(p) || isRatioUnresolved(p));
  const n = stillFlagged.length;
  const m = cards.length;
  const anyBlocked = cards.some(isBlockedPrint);

  // Cards a bulk crop/fit can act on: the photo genuinely mismatches the size.
  // Geometry-based (not printCheck) so crop ↔ fit keeps toggling after resolve.
  const ratioCards = cards.filter((p) => {
    if (isBlockedPrint(p)) return false;
    const photo = photoById.get(p.photoId);
    const entry = actions.catalog.get(p.sizeId);
    if (!photo || !entry || !(photo.width > 0) || !(photo.height > 0)) return false;
    return (
      ratioFit(photo.width, photo.height, entry.inches, actions.thresholds.ratioTolerance) ===
      'autocrop'
    );
  });

  /** Bulk: centered auto-crop everywhere — cards you dragged keep their custom crop. */
  const autoCropAll = () => {
    for (const p of ratioCards) {
      const photo = photoById.get(p.photoId);
      const entry = actions.catalog.get(p.sizeId);
      if (reviewCardState(p, photo, entry?.inches).chip === 'Custom crop') continue;
      actions.setCrop(p.id);
    }
  };
  /** Bulk: whole photo + white space everywhere (explicit, replaces custom crops). */
  const fitAll = () => {
    for (const p of ratioCards) actions.setFit(p.id);
  };
  /** The exception: confirm every still-open card and close immediately. */
  const acceptAll = () => {
    for (const p of cards) {
      if (isBlockedPrint(p)) actions.printAnyway(p.id);
      else if (isRatioUnresolved(p)) actions.setCrop(p.id);
    }
    onClose();
  };

  const scoped = !!scopeIds;
  const from = scoped ? ' from your bulk setup' : '';
  const sub =
    m === 0
      ? 'All done. Nothing needs your attention.'
      : n > 0
        ? `${n} of ${m}${from} still ${n === 1 ? 'needs' : 'need'} a look. Fix each one, or all at once. Changes save instantly.`
        : `All ${m}${from} sorted. Check the previews, then press Done.`;

  return (
    <ModalShell onClose={onClose} width="96vw" label="Review auto-cropped prints" className="review">
      <div className="review__head">
        <div>
          <div className="review__title">Review crops</div>
          <div className="review__sub">{sub}</div>
        </div>
        <button type="button" className="review__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      {m > 0 && (
        <div className="review__bulk" role="group" aria-label="Bulk review actions">
          <span className="review__bulk-label">All at once:</span>
          {ratioCards.length > 0 && (
            <>
              <button type="button" className="pill-btn" onClick={autoCropAll}>
                Auto-crop all
              </button>
              <button type="button" className="pill-btn" onClick={fitAll}>
                Fit all
              </button>
            </>
          )}
          {n > 0 && (
            <button type="button" className="pill-btn" onClick={acceptAll}>
              Accept all{anyBlocked ? ' (even low quality)' : ''}
            </button>
          )}
        </div>
      )}

      {m === 0 ? (
        <div className="review__empty">
          <span className="review__empty-tick" aria-hidden>
            ✓
          </span>
          All crops confirmed. Nothing needs your attention.
        </div>
      ) : (
        <div className="review__grid">
          {cards.map((p) => (
            <ReviewCard
              key={p.id}
              print={p}
              photo={photoById.get(p.photoId)}
              actions={actions}
              onOpenCrop={onOpenCrop}
            />
          ))}
        </div>
      )}

      <div className="review__foot">
        <span className="review__foot-note">
          {n > 0
            ? 'Anything you don’t confirm still prints with the crop shown.'
            : m > 0
              ? 'All saved. You can still change any card.'
              : ''}
        </span>
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Done
        </button>
      </div>
    </ModalShell>
  );
}
